import { ArrowRight, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "../data/site";
import EntertainmentCenter from "./EntertainmentCenter";
import FlyWords from "./FlyWords";
import "../styles/entertainment-center.css";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full bg-[#050505] overflow-hidden grain"
    >
      {/* --------- Wall stage (fixed height, self-contained) --------- */}
      <div
        className="relative w-full overflow-hidden"
        style={{ height: "min(62svh, 560px)" }}
        data-testid="hero-stage"
      >
        {/* Oversized inner canvas so the wall art fills more of the visible stage
            (the entertainment-center scene has empty floor space at ~55% down;
            rendering it at ~180% height pushes the couch/floor to the stage bottom) */}
        <div
          className="absolute inset-x-0 top-0"
          style={{ height: "180%" }}
          data-testid="hero-stage-canvas"
        >
          <EntertainmentCenter />
        </div>
        <div className="absolute inset-0 z-[6] pointer-events-none hero-vignette" />

        {/* Centered brand plaque above the TV */}
        <div
          className="absolute left-1/2 -translate-x-1/2 z-20 px-4 top-[118px] sm:top-[100px] md:top-[92px] text-center w-full"
          data-testid="hero-overline-wrap"
        >
          <div className="mx-auto h-px w-12 md:w-14 bg-[#D4AF37] opacity-80 mb-2 md:mb-3" />
          <div
            className="text-[11px] sm:text-[12px] md:text-sm tracking-[0.22em] sm:tracking-[0.28em] md:tracking-[0.32em] uppercase font-semibold text-[#F3E5AB] whitespace-nowrap"
            data-testid="hero-overline"
            style={{
              textShadow:
                "0 2px 14px rgba(0,0,0,0.95), 0 0 22px rgba(0,0,0,0.7), 0 0 30px rgba(212,175,55,0.25)",
            }}
          >
            Custom Luxury Wall Specialists
          </div>
          <div className="mx-auto h-px w-12 md:w-14 bg-[#D4AF37] opacity-80 mt-2 md:mt-3" />
        </div>

        {/* Soft fade so the stage blends into the block below */}
        <div
          className="absolute inset-x-0 bottom-0 h-16 pointer-events-none z-[7]"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,5,0) 0%, rgba(5,5,5,0.85) 60%, #050505 100%)",
          }}
        />
      </div>

      {/* --------- Heading block (clean, below the stage) --------- */}
      <div
        className="relative z-10 container-x px-5 md:px-12 lg:px-24 pt-4 pb-14 md:pt-6 md:pb-20"
        data-testid="hero-content"
      >
        <div className="max-w-3xl mx-auto sm:mx-0 text-center sm:text-left">
          <FlyWords
            as="h1"
            dataTestid="hero-title"
            className="font-display text-[2.4rem] leading-[1.05] sm:text-5xl md:text-5xl lg:text-6xl font-light tracking-tight text-white"
            segments={[
              { text: "A wall should make" },
              { type: "break" },
              { text: "a statement.", className: "italic text-[#D4AF37]" },
            ]}
          />
          <p
            data-testid="hero-subtitle"
            className="mt-4 sm:mt-5 text-sm sm:text-base text-[#D1D1D6] max-w-2xl mx-auto sm:mx-0 leading-relaxed font-light"
          >
            Custom media walls, feature installations and architectural focal
            points across Tampa Bay — precision craftsmanship, premium
            materials.
          </p>

          <div className="mt-6 sm:mt-7 flex flex-wrap gap-3 sm:gap-4 justify-center sm:justify-start">
            <Link
              to="/design-my-wall"
              data-testid="hero-cta-design"
              className="btn-gold !py-3 !px-5 text-sm sm:text-base sm:!py-3.5 sm:!px-7"
            >
              Design My Wall
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="#contact"
              data-testid="hero-cta-quote"
              className="btn-ghost !py-3 !px-5 text-sm sm:text-base sm:!py-3.5 sm:!px-7"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden sm:inline">Request Consultation</span>
              <span className="sm:hidden">Consultation</span>
            </a>
          </div>

          <div
            className="mt-10 grid grid-cols-3 gap-4 sm:gap-6 max-w-xl mx-auto sm:mx-0"
            data-testid="hero-stats"
          >
            <Stat n="100%" label="Custom built" />
            <Stat n="Florida" label="Tampa Bay area" />
            <Stat n="A–Z" label="Design + install" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display text-2xl sm:text-3xl md:text-4xl gold-text">
        {n}
      </div>
      <div className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.22em] uppercase text-[#A1A1AA] mt-1">
        {label}
      </div>
    </div>
  );
}
